import React from 'react';
import moment from 'moment';
import { Text, TextAs, TextSizes } from '@clockout-dev/design-elements';

/**
 * Small visual helper for "2 mins ago" / "6 hours ago" / "2 days ago"
 * strings in the LAST ACTIVITY / DATE columns.
 *
 * Accepts strings parseable by `moment`, `moment` instances or `Date`
 * objects. When the value cannot be parsed or is missing, renders the
 * placeholder (`—` by default) so the column is not left blank.
 *
 * @param {Object} props
 * @param {string | moment.Moment | Date | null | undefined} props.value
 * @param {'fromNow' | 'absolute'} [props.format='fromNow']
 * @param {string} [props.absoluteFormat='MMM Do, YYYY']
 * @param {string} [props.placeholder='—']
 * @param {string} [props.className]
 */
function RelativeTime({
    value,
    format = 'fromNow',
    absoluteFormat = 'MMM Do, YYYY',
    placeholder = '—',
    className,
}) {
    if (value == null || value === '') {
        return (
            <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={className}>
                {placeholder}
            </Text>
        );
    }

    const m = moment(value);
    if (!m.isValid()) {
        return (
            <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={className}>
                {placeholder}
            </Text>
        );
    }

    // `m.fromNow()`, not the `formatRelativeAge` util: the util parses with
    // `moment.parseZone`, which reads an offset-less timestamp as a wall clock
    // rather than as local time. The two agree on every offset-bearing value,
    // so routing through it would buy nothing here and would quietly shift the
    // Reports tables this component also serves.
    const label = format === 'absolute' ? m.format(absoluteFormat) : m.fromNow();
    return (
        <Text
            as={TextAs.SPAN}
            size={TextSizes.SMALL}
            className={className}
            title={m.format('LLL')}
        >
            {label}
        </Text>
    );
}

export default RelativeTime;
